import CookieOption, {CookieOptionType} from './cookiesConsent/cookieOption';
import CookiesModal, {PageType} from './cookiesConsent/components/cookiesModal';

const COOKIE_CONSENT = {
    name: 'cookiesConsent',
    lifetime: 365 * 86400,
};

interface OptionChangeData {
    options: CookieOption[];
}

export default class CookiesConsent {

    static EVENT_OPTIONS_CHANGE_AFTER = 'aiticookiesconsent_options_change_after';

    private readonly _options: CookieOption[] = [];
    private readonly _initDeferred: JQuery.Deferred<void> = jQuery.Deferred();
    private _modal = React.createRef<CookiesModal>();

    constructor() {
        this.changeOptions = this.changeOptions.bind(this);

        this.onOptionsChangeAfter(() => {
            this._updateConsentCookie();
            this._sendGtmConsentUpdate();
        });

        this._initOptions();
    }

    private _initOptions() {
        let cookieValue = AitiCore.getCookies().get(COOKIE_CONSENT.name, true);

        this._options.push(new CookieOption(
            CookieOptionType.ANALYTICS,
            _('[COOKIES_MODAL] Analytical cookies'),
            _('[COOKIES_MODAL] ANALYTICAL DESCRIPTION'),
            !!Number(cookieValue?.[CookieOptionType.ANALYTICS] ?? 0),
        ));

        this._options.push(new CookieOption(
            CookieOptionType.MARKETING,
            _('[COOKIES_MODAL] Marketing cookies'),
            _('[COOKIES_MODAL] MARKETING DESCRIPTION'),
            !!Number(cookieValue?.[CookieOptionType.MARKETING] ?? 0),
        ));

        if (AitiCore.getCookies().exists(COOKIE_CONSENT.name)) {
            for (let option of this.getOptions()) {
                this._changeOption(option.getType(), option.isAllowed());
            }
        }
    }

    private _renderModal() {
        if (this._modal.current) {
            return;
        }

        ReactDOM.render(
            <CookiesModal
                cookieOptions={Object.assign([], this.getOptions())}
                onChangeCookieOptions={this.changeOptions}
                ref={this._modal}
            />,
            $j('<div/>').appendTo('body')[0]
        );
    }

    private _updateConsentCookie() {
        let value = {};

        for (let option of this.getOptions()) {
            value[option.getType()] = Number(option.isAllowed());
        }

        AitiCore.getCookies().set(COOKIE_CONSENT.name, value, {
            expires: COOKIE_CONSENT.lifetime,
            objectEncode: true,
        });
    }

    protected _sendGtmConsentUpdate() {
        AitiCore.getClients().getGtmClient().push('consent', 'update', {
            'analytics_storage': this.getOption(CookieOptionType.ANALYTICS).isAllowed() ? 'granted' : 'denied',
            'ad_storage': this.getOption(CookieOptionType.MARKETING).isAllowed() ? 'granted' : 'denied',
        });

        AitiCore.getClients().getGtmClient().push('event', 'consentUpdate');
    }

    init(enabled: boolean) {
        if (!enabled) {
            for (let option of this.getOptions()) {
                this._changeOption(option.getType(), true);
            }

            this._sendGtmConsentUpdate();

            this._initDeferred.resolve();
            return;
        }

        if (!AitiCore.getCookies().exists(COOKIE_CONSENT.name)) {
            this.showModal();
        } else {
            this._sendGtmConsentUpdate();
        }

        this._initDeferred.resolve();
    }

    showModal() {
        this._renderModal();
        this._modal.current.show();
    }

    showSettings() {
        this._renderModal();
        this._modal.current.show(PageType.SETTINGS);
    }

    getOptions(): CookieOption[] {
        return this._options;
    }

    getOption(type: CookieOptionType): CookieOption | null {
        for (let option of this._options) {
            if (option.getType() == type) {
                return option;
            }
        }

        return null;
    }

    changeOptions(options: CookieOption[]) {
        for (let option of options) {
            this._changeOption(option.getType(), option.isAllowed());
        }

        DeferredEvents.trigger(CookiesConsent.EVENT_OPTIONS_CHANGE_AFTER, {
            options: this.getOptions()
        });
    }

    protected _changeOption(type: CookieOptionType, allowed: boolean) {
        let option = this.getOption(type);
        if (option) {
            option.setAllowed(allowed);
        }
    }

    onInitAfter(handler: (options: CookieOption[]) => void) {
        this._initDeferred.done(() => {
            handler(this.getOptions());
        });
    }

    onOptionsChangeAfter(handler: DeferredEventHandler<OptionChangeData>, priority: number = DeferredEvents.PRIORITY_NORMAL, ignoreCancel: boolean = false) {
        DeferredEvents.listen(CookiesConsent.EVENT_OPTIONS_CHANGE_AFTER, handler, priority, ignoreCancel, this);
        return this;
    }
}

declare global {
    const AitiCookiesConsent: CookiesConsent;
}

/**
 * @type {CookiesConsent}
 */
global.AitiCookiesConsent = new CookiesConsent();
